import { UNIT_TYPES, rackDirectionMapping, colorsByObjectType } from "./constant";

import { round } from "lodash";
import { eventEmiterInstance } from "./EventEmitter";

// Generating unique id for each element
export const uuid = (length = 5) => {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);

  return Array.from(bytes, b => chars[b % chars.length]).join("");
};


export const pixelsToUnits = (pixel, unit = UNIT_TYPES.inch, options = {}) => {
  // check if unit is in unit types
  const { inInPixels } = options;
  const unitValues = Object.values(UNIT_TYPES);
  if (!unitValues.includes(unit)) {
    throw new Error("unknown unit");
  }
  const inchInPixels = inInPixels || 1;
  const unitRatio = {
    [UNIT_TYPES.feet]: 12 * inchInPixels,
    [UNIT_TYPES.inch]: inchInPixels
  };
  return pixel / unitRatio[unit];
};

export const unitsToPixels = (units, unit = UNIT_TYPES.inch, options = {}) => {
  // check if unit is in unit types
  const { inInPixels } = options;
  const unitValues = Object.values(UNIT_TYPES);
  if (!unitValues.includes(unit)) {
    throw new Error("unknown unit");
  }
  const inchInPixels = inInPixels || 1;
  const unitRatio = {
    [UNIT_TYPES.feet]: 12 * inchInPixels,
    [UNIT_TYPES.inch]: inchInPixels
  };
  return units * unitRatio[unit];
};




export function sortBinsByPosition(bins = []) {
  // First, identify unique y-coordinates to determine levels
  const uniqueYs = [...new Set(bins?.map(bin => bin?.positionY))].sort((a, b) => a - b).reverse();

  // Create a map of y-coordinate to level number
  const yToLevel = Object.fromEntries(uniqueYs?.map((y, index) => [y, index + 1]));

  // Sort bins first by level (y-coordinate) and then by x-coordinate
  return bins?.sort((a, b) => {
    // Get level numbers for both bins
    const levelA = yToLevel[a.positionY];
    const levelB = yToLevel[b.positionY];

    // If levels are different, sort by level
    if (levelA !== levelB) {
      return levelA - levelB;
    }

    // If on same level, sort by x-coordinate
    return a.positionX - b.positionX;
  });
}


// Tranform elements from response data to the required format for the layout editor elements
export const transformElements = (responseData) => {
  return responseData.map((obj) => {
    return transformElement(obj);
  });
};

export const transformElement = (obj) => {
  // if (obj?.objectType !== 'wall') {
  obj.positionX = parseFloat(obj.positionX);
  obj.positionY = parseFloat(obj.positionY);
  obj.width = parseFloat(obj.width);
  obj.length = parseFloat(obj.length);

  obj.positionX = round(unitsToPixels(obj.positionX, obj.unitType), 2);
  obj.positionY = round(unitsToPixels(obj.positionY, obj.unitType), 2);
  obj.width = round(unitsToPixels(obj.width, obj.unitType), 2);
  obj.length = round(unitsToPixels(obj.length, obj.unitType), 2);
  obj.rotation = obj.orientation || 0;
  // }

  const coordinates = {
    x1: obj?.positionX,
    y1: obj?.positionY,
    x2: obj?.positionX + obj?.width,
    y2: obj?.positionY + obj?.length,
    hasElementChanges: false,
  };

  // const rackWidth = coordinates.x2 - coordinates.x1;
  // const rackHeight = coordinates.y2 - coordinates.y1;

  // let rackView = rackWidth > rackHeight ? SouthToNorth : EastToWest;

  let element = {};
  if (obj?.objectType === "rack") {
    //switch the directions based on the facing direction
    const rackView = { rackViewing: [], label: obj.facingDirection };
    rackView.rackViewing = rackView.label.split("").map((label) => rackDirectionMapping[label]);

    element = {
      ...obj,
      rackView: rackView,
      facingDirection: rackView?.label,
      ...coordinates,
    };

    const transforedBins = element?.bins?.map((bin) => {
      bin.positionX = parseFloat(bin.positionX);
      bin.positionX = round(unitsToPixels(bin.positionX, element.unitType), 2);

      bin.positionY = parseFloat(bin.positionY);
      bin.positionY = round(unitsToPixels(bin.positionY, element.unitType), 2);

      bin.width = parseFloat(bin.width);
      bin.width = round(unitsToPixels(bin.width, element.unitType), 2);

      bin.height = parseFloat(bin.height);
      bin.height = round(unitsToPixels(bin.height, element.unitType), 2);
      bin.x1 = bin.positionX;
      bin.x2 = bin.positionX + bin.width;
      bin.y1 = bin.positionY;
      bin.y2 = bin.positionY + bin.height;

      return {
        ...bin,
        type: "rectangle",
        objectType: "bin",
        binCode: bin.code,
        rackCode: bin.name,
        x: bin.positionX,
        y: bin.positionY,
        // level: level.order,
        isBinDeleted: false,
        color: obj.color
      };
    });

    // element["levels"] = updatedLevels;
    element["bins"] = transforedBins;

    if (element?.objectType === "rack") {
      return {
        ...element,
        type: "rectangle",
      };
    } else if (element?.objectType === "line") {
      return { ...element, type: "line" };
    }
  } else if (obj?.objectType === "area" || obj?.objectType === "dock" || obj?.objectType === "wall" || obj?.objectType === "zone") {
    const rackView = { rackViewing: [], label: obj.facingDirection };
    rackView.rackViewing = rackView?.label?.split("")?.map((label) => rackDirectionMapping[label]) || null;
    let _element = {
      ...obj,
      x: coordinates.x1,
      y: coordinates.y1,
      ...coordinates,
      color: obj.color || colorsByObjectType[obj.objectType],
      rackView: rackView,
      facingDirection: rackView?.label,
      hasElementChanges: false,
      type: "rectangle",
    };
    if (_element?.objectType === "wall") {
      return { ..._element, type: "line" };
    }

    if (_element?.objectType === "area" && _element?.subAreas?.length) {
      let _subAreas = _element?.subAreas?.map((subArea) => {
        let _x = parseFloat(subArea?.x);
        let _y = parseFloat(subArea?.y);
        let _width = parseFloat(subArea?.width);
        let _height = parseFloat(subArea?.height);

        let _positionX = round(unitsToPixels(_x, subArea?.unitType), 2);
        let _positionY = round(unitsToPixels(_y, subArea?.unitType), 2);
        let subAreaWidth = round(unitsToPixels(_width, subArea?.unitType), 2);
        let subAreaHeight = round(unitsToPixels(_height, subArea?.unitType), 2);
        return {
          ...subArea,
          width: subAreaWidth,
          height: subAreaHeight,
          positionX: _positionX,
          positionY: _positionY,
          x1: _positionX,
          y1: _positionY,
          x2: _positionX + subAreaWidth,
          y2: _positionY + subAreaHeight,
          objectType: "subArea",
        };
      });
      _element["subAreas"] = _subAreas;
    }
    return _element;
  }
};

//onSaveRack
export const onSaveRack = (rack, formData, warehouseLayoutId) => {
  const {
    name,
    code,
    width,
    color,
    type,
    facingDirection,
    x,
    y,
    x1,
    y1,
    x2,
    y2,
    levels,
    columns,
    objectType,
    height,
    id,
    hasElementChanges,
    zIndex,
    unitType,
  } = rack;

  let payload = {};

  const rackWidth = x2 - x1;
  const rackHeight = y2 - y1;
  if (objectType === "rack") {
    const transformedLevels = levels.map((level, levelIndex) => {
      const transformedBins = level?.bins?.map((bin, index) => {
        return {
          id: bin.id || null,
          name: bin.name,
          code: bin.binCode,
          rackId: level?.rackId || null,
          levelId: bin.levelId || null,
          columnId: bin.columnId || index,
          width: round(pixelsToUnits(bin.width, formData.unitType), 2),
          height: round(pixelsToUnits(bin.height, formData.unitType), 2),
          depth: bin.depth || 0,
          positionX: round(pixelsToUnits(bin.x, formData.unitType), 2),
          positionY: round(pixelsToUnits(bin.y, formData.unitType), 2),
          level: level.order,
          column: bin.column || null,
          order: bin.order || levelIndex,
        };
      });
      return {
        ...level,
        height: round(pixelsToUnits(level.levelHeight, formData.unitType), 2),
        bins: transformedBins,
      };
    });
    const len = rackWidth > rackHeight ? formData.rackHeight : formData.rackWidth;
    const wid = rackWidth > rackHeight ? formData.rackWidth : formData.rackHeight;

    payload = {
      id: isNaN(+id) ? null : id,
      name: formData.rackName,
      code: formData.rackCode,
      warehouseLayoutId,
      positionX: round(formData.rackX, 2),
      //positionX: round(unitsToPixels(formData.rackX, formData.unitType), 2), //TODO: ROUND OFF TO 2 DECIMAL PLACES
      positionY: round(formData.rackY, 2),
      //positionY: round(unitsToPixels(formData.rackY, formData.unitType), 2), //TODO: ROUND OFF TO 2 DECIMAL PLACES
      length: round(len, 2),
      //length: round(unitsToPixels(len, formData.unitType), 2), //TODO: ROUND OFF TO 2 DECIMAL PLACES
      width: round(wid, 2),
      //width: round(unitsToPixels(wid, formData.unitType), 2), //TODO: ROUND OFF TO 2 DECIMAL PLACES
      orientation: round(rack?.rotation || rack?.orientation || 0, 2),
      rotation: round(rack?.rotation || rack?.orientation || 0, 2),
      facingDirection: facingDirection || rackDirection?.label,
      color,
      hasElementChanges,
      zIndex,
      objectType,
      levels: transformedLevels,
      columns,
      unitType: formData.unitType,
    };
  } else if (objectType === "wall" || objectType === "area" || objectType === "dock" || objectType === "zone") {
    payload = {
      code: formData.rackCode,
      color,
      id: isNaN(+id) ? null : id,
      length: round(formData.rackHeight, 2),
      //length: round(unitsToPixels(formData.rackHeight, formData.unitType), 2), //TODO: ROUND OFF TO 2 DECIMAL PLACES
      width: round(formData.rackWidth, 2),
      //width: round(unitsToPixels(formData.rackWidth, formData.unitType), 2), //TODO: ROUND OFF TO 2 DECIMAL PLACES
      name: formData.rackName,
      positionX: round(formData.rackX, 2),
      //positionX: round(unitsToPixels(formData.rackX, formData.unitType), 2), //TODO: ROUND OFF TO 2 DECIMAL PLACES
      positionY: round(formData.rackY, 2),
      //positionY: round(unitsToPixels(formData.rackY, formData.unitType), 2), //TODO: ROUND OFF TO 2 DECIMAL PLACES
      warehouseLayoutId,
      zIndex,
      objectType,
      unitType: formData.unitType,
      orientation: round(rack?.rotation || rack?.orientation || 0, 2),
      rotation: round(rack?.rotation || rack?.orientation || 0, 2),
    };
  }
  return payload;
};

export const _onSaveAllElements = async (warehouseLayoutId, elements) => {
  setLoading(true);
  setSaveAllLoader(true);
  //Get the elements that have changes
  const modifiedElements = elements.filter((element) => element.hasElementChanges);

  //transform the elements to save
  const elementsToSave = modifiedElements.map((element) => {
    const { name, code, color, type, facingDirection, x, y, x1, y1, x2, y2, objectType, height, id, hasElementChanges, zIndex, width, unitType } = element;

    if (element.objectType === "rack") {
      const transformedLevels =
        element?.levels?.map((level, levelIndex) => {
          const transformedBins =
            level?.bins?.map((bin, index) => {
              return {
                id: bin.id || null,
                name: bin.name,
                code: bin.binCode,
                rackId: level.rackId || null,
                levelId: bin.levelId || null,
                columnId: bin.columnId || index,
                width: round(pixelsToUnits(bin.width, element.unitType), 2),
                height: round(pixelsToUnits(bin.height, element.unitType), 2),
                depth: bin.depth || 0,
                positionX: round(pixelsToUnits(bin.x, element.unitType), 2),
                positionY: round(pixelsToUnits(bin.y, element.unitType), 2),
                level: level.order,
                column: bin.column || null,
                order: bin.order || levelIndex,
              };
            }) || [];
          return {
            ...level,
            id: level.id || null,
            name: level.name || null,
            code: level.code,
            height: round(pixelsToUnits(level.levelHeight, element.unitType), 2),
            //levelWidth: level.levelWidth,
            levelY: round(pixelsToUnits(level.levelY, element.unitType), 2),
            bins: transformedBins,
          };
        }) || [];

      let positionX = round(element.x1, 2);
      positionX = pixelsToUnits(positionX, element.unitType);

      let positionY = round(element.y1, 2);
      positionY = pixelsToUnits(positionY, element.unitType);

      const rackWidth = round(x2 - x1, 2);
      const rackHeight = round(y2 - y1, 2);

      let width = element?.width || rackWidth;
      let length = element?.length || rackHeight;

      // if(element?.width && element?.length){
      //   length = rackWidth > rackHeight ? element.length : element.width;
      //   width = rackWidth > rackHeight ? element.width : element.length;
      // }

      length = round(y2 - y1, 2);
      length = pixelsToUnits(length, element.unitType);

      width = round(x2 - x1, 2);
      width = pixelsToUnits(width, element.unitType);

      return {
        id: isNaN(+element.id) ? null : element.id,
        name: element.name,
        code: element.code,
        warehouseLayoutId: warehouseLayoutId,
        positionX,
        positionY,
        length,
        width,
        orientation: round(element?.rotation || element?.orientation || 0, 2),
        rotation: round(element?.rotation || element?.orientation || 0, 2),
        color: element.color,
        // type: element.type,
        facingDirection: element.rackView.label,
        objectType: element.objectType,
        // id: element.id,
        hasElementChanges: element.hasElementChanges,
        zIndex: element.zIndex,
        levels: transformedLevels,
        columns: element.columns || [],
        unitType: element.unitType,
      };
    } else if (objectType === "wall" || objectType === "area" || objectType === "dock" || objectType === "zone") {
      let objectWidth = x2 - x1;
      objectWidth = round(objectWidth, 2);
      objectWidth = pixelsToUnits(objectWidth, element.unitType);

      let objectLength = y2 - y1;
      objectLength = round(objectLength, 2);
      objectLength = pixelsToUnits(objectLength, element.unitType);

      let positionX = round(x1, 2);
      positionX = pixelsToUnits(positionX, element.unitType);

      let positionY = round(y1, 2);
      positionY = pixelsToUnits(positionY, element.unitType);

      return {
        id: isNaN(+element.id) ? null : element.id,
        name,
        code,
        warehouseLayoutId: location?.state?.warehouseLayoutId,
        positionX,
        positionY,
        length: objectLength,
        width: objectWidth,
        zIndex,
        objectType,
        unitType,
        color,
        hasElementChanges,
        orientation: round(element?.rotation || element?.orientation || 0, 2),
        rotation: round(element?.rotation || element?.orientation || 0, 2),
      };
    }
  });

  try {
    const { data } = await axios.post("layout-objects/save-all", elementsToSave);
    setSaveAllLoader(false);
    toast.success("Rack saved successfully");
    setShowShelf(false);
    setLoading(false);
    fetchLayoutObjects();
  } catch (error) {
    const {
      response: {
        data: { message },
      },
    } = error;
    const defaultErrorMessage = "Oops Something went wrong";
    toast.error(message || defaultErrorMessage);
    console.log("error", error);
  }
};



export const binHandler = (rack, levelObj, selectedLevel) => {
  const { levels } = rack;
  //alert('Bin added successfully');
  const isObject = levelObj && Object.keys(levelObj)?.length > 0;

  if (isObject) {
    const selectedLevelIndex = levels.findIndex((level) => level?.id === selectedLevel?.id);
    const prevLevel = levels[selectedLevelIndex - 1];

    const level = levels[selectedLevelIndex];
    level.bins = level.bins || [];

    const binCode = levelObj?.binCode;
    const binsWidth = level?.bins?.reduce((accumulator, bin) => accumulator + round(bin.width, 2), 0);

    // Exclude the last object in the array
    const levelsExcludingLast = levels.slice(0, -1);

    // Calculate the sum of levelHeight values excluding the last object
    const sumLevelHeights = levelsExcludingLast.reduce((sum, level) => sum + parseInt(level.levelHeight), 0);

    //::
    const binHeight =
      prevLevel?.bins[level.bins?.length ? level.bins?.length : 0]?.y + prevLevel?.bins[level.bins?.length ? level.bins?.length : 0]?.height ||
      selectedLevel?.levelY;

    const bin = {
      //id: `${rack?.code}-L${levels.length}-C${level.bins?.length}`,
      id: null,
      binCode: levelObj?.binCode || `${rack?.code}-L${selectedLevel.order}-C${level.bins?.length}`,
      columnId: `C${level.bins?.length}`,
      level: selectedLevel.order,
      name: levelObj?.name,
      level: selectedLevel.order,
      column: null,
      x: binsWidth,
      y: binHeight, //selectedLevel?.levelY,
      width: round(levelObj?.width, 2),
      height: round(selectedLevel?.levelHeight, 2),
      isBinDeleted: false,
    };
    level.bins.push(bin);

    const updatedLevels = [...levels];
    updatedLevels[selectedLevelIndex] = level;
    // setLevels(updatedLevels);

    const updatedRack = {
      ...rack,
      levels: updatedLevels,
    };
    return updatedRack;
    // dispatch({ type: "UPDATE_ELEMENT", payload: updatedRack });
  }
  // setaddBinToggler(!addBinToggler);
};


//GenerateBins Functionality
export const generateBins = (data, levels, columns, binWidth, binHeight) => {
  const { x1, x2, y1, y2, code } = data;

  const levelsArray = [];
  const columnItems = [];
  const rackWidth = x2 - x1;
  const rackHeight = y2 - y1;
  const width = rackWidth > rackHeight ? rackWidth : rackHeight;

  for (let i = 0; i < levels; i++) {
    const rackWidth = x2 - x1;
    const rackHeight = y2 - y1;
    const levelItems = {
      name: `Level ${i}`,
      id: uuid(),
      order: i,
      code: `L${i}`,
      levelHeight: binHeight,
      levelWidth: width,
      levelY: i * binHeight,
      bins: [], // Array to hold bins for this level
    };

    for (let j = 0; j < columns; j++) {
      const bin = {
        id: `${code}-L${i}-C${j}`,
        binCode: `${code}-L${i}-C${j}`,
        columnId: `C${j}`,
        name: code,
        level: i,
        column: j,
        x: j * binWidth,
        y: i * binHeight,
        width: binWidth,
        height: binHeight,
        isBinDeleted: false,
      };
      levelItems.bins.push(bin);
    }

    levelsArray.push(levelItems);
  }

  for (let i = 0; i < columns; i++) {
    columnItems.push({
      name: `Column ${i + 1}`,
      id: i,
      order: i,
      code: `C${i + 1}`,
      colWidth: binWidth,
    });
  }

  return { levels: levelsArray, columns: columnItems };
};



export const highlightLocations = (locations, color) => {
  if (locations && locations.length > 0) {
    const payload = { locationCodes: locations, primaryColor: color || '#0BDA51' };
    eventEmiterInstance.emit('hideRacks', payload);
  } else {
    eventEmiterInstance.emit('hideRacks', null);
  }
};
//check if bin codes have duplicates
export const getDuplicateCodes = (codes) => {
  const duplicates = codes.filter((code, index) => codes.indexOf(code) !== index);
  return [...new Set(duplicates)];
}
